import { useEffect, useMemo, useRef, useState } from "react";
import MapView from "@/components/MapView";
import CockpitMiniMap from "@/components/CockpitMiniMap";
import { buildReplayNav } from "@/lib/mapNavigation";

// SKYCORE MAP.NAV.1 — LABORATOIRE CARTOGRAPHIQUE (prototype du moteur de rotation + captures).
// DONNÉES DÉMO LOCALES uniquement : aucune télémétrie, aucun appel API, aucune commande drone.
const C = { lat: 49.4944, lng: 0.1079 };
const off = (dLatM, dLngM) => ({ lat: C.lat + dLatM / 111320, lng: C.lng + dLngM / (111320 * Math.cos((C.lat * Math.PI) / 180)) });
const WP = [off(180, 120), off(260, -140), off(40, -260)];
const HOME = { ...off(-120, -60), altAmsl: 12.3, ageS: 0.4 };
const BASE = { ...off(-160, 150), title: "Base configurée", label: "Hangar" };
const REPLAY = Array.from({ length: 12 }, (_, i) => ({ ...off(-200 + i * 0, -200 + i * 6), timestamp: new Date(Date.UTC(2026, 5, 1, 12, 0, i * 2)).toISOString(), speed: 3, alt: 20 }));

function Btn({ id, on, onClick, children }) {
  return <button data-testid={id} onClick={onClick} className={`px-2 py-1 rounded-sm border font-mono text-[10px] uppercase ${on ? "border-[#2DD4BF] text-[#2DD4BF] bg-[#2DD4BF]/10" : "border-white/15 text-white/70 hover:bg-white/5"}`}>{children}</button>;
}

export default function MapNavLab() {
  const [heading, setHeading] = useState(90);
  const [follow, setFollow] = useState(false);
  const [motion, setMotion] = useState("none");
  const [stale, setStale] = useState(false);
  const [replayHeading, setReplayHeading] = useState(false);
  const [showMini, setShowMini] = useState(true);
  const [noFix, setNoFix] = useState(false);
  const [posInvalid, setPosInvalid] = useState(false);
  const [showDemo2, setShowDemo2] = useState(true);
  const [tick, setTick] = useState(0);
  const [probe, setProbe] = useState(null);
  const [map, setMap] = useState(null);
  const cockpitRef = useRef(null);
  const posRef = useRef({ ...C, n: 0 });

  useEffect(() => {
    if (stale) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [stale]);

  if (motion === "derived" && !stale) posRef.current = { ...off(0, (tick % 60) * 4), n: tick };
  const pos = motion === "derived" ? posRef.current : C;
  const lastSeen = useMemo(() => new Date().toISOString(), [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const drone = {
    drone_id: "DEMO-1", status: "online", lat: posInvalid ? null : pos.lat, lng: posInvalid ? null : pos.lng, heading, battery: 80, relative_alt: 25, mode: "POSCTL",
    gps_state: noFix ? "no_fix" : "fix", fix_type: noFix ? 1 : 3, gps_fix: !noFix, last_seen: stale ? "2026-01-01T00:00:00Z" : lastSeen,
    speed: motion === "none" ? 0.1 : 4, course_over_ground_deg: motion === "measured" ? 90 : undefined,
    ...(motion === "measured" ? { course_source: "EKF_VELOCITY", course_age_s: 0.2, course_speed_mps: 4, course_observed_at: lastSeen } : {}),
  };
  const drone2 = { drone_id: "DEMO-2", status: "online", ...off(-60, 220), heading: 200, speed: 0, gps_state: "fix", last_seen: lastSeen, battery: 55 };
  // MAP.NAV.3 — drone près du bord supérieur (popup en limite de cadre).
  const drone3 = { drone_id: "DEMO-3", status: "online", ...off(820, -300), heading: 0, speed: 0, gps_state: "fix", last_seen: lastSeen, battery: 91 };
  const replayPts = REPLAY.map((p) => (replayHeading ? { ...p, heading: 45 } : p));
  const cursorNav = buildReplayNav(replayPts, replayPts.length - 1);

  useEffect(() => {
    if (!map) return undefined;
    const onClick = (e) => setProbe({ lat: e.latlng.lat.toFixed(5), lng: e.latlng.lng.toFixed(5), x: Math.round(e.containerPoint.x), y: Math.round(e.containerPoint.y) });
    map.on("click", onClick);
    return () => map.off("click", onClick);
  }, [map]);

  return (
    <div data-testid="map-nav-lab" className="h-full flex flex-col gap-2 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] px-2 py-0.5 rounded-sm bg-[#C084FC] text-black uppercase">Laboratoire carto — données démo</span>
        {[0, 90, 180, 270, 359].map((h) => <Btn key={h} id={`lab-heading-${h}`} on={heading === h} onClick={() => setHeading(h)}>{h}°</Btn>)}
        <Btn id="lab-heading-0-after-359" onClick={() => { setHeading(359); setTimeout(() => setHeading(0), 900); }}>359→0</Btn>
        <Btn id="lab-follow" on={follow} onClick={() => setFollow((f) => !f)}>Suivre</Btn>
        <Btn id="lab-motion-none" on={motion === "none"} onClick={() => setMotion("none")}>Immobile</Btn>
        <Btn id="lab-motion-measured" on={motion === "measured"} onClick={() => setMotion("measured")}>Est (COG mesuré)</Btn>
        <Btn id="lab-motion-derived" on={motion === "derived"} onClick={() => setMotion("derived")}>Est (dérivé GPS)</Btn>
        <Btn id="lab-stale" on={stale} onClick={() => setStale((s) => !s)}>Périmé</Btn>
        <Btn id="lab-gps-nofix" on={noFix} onClick={() => setNoFix((v) => !v)}>GPS NO_FIX</Btn>
        <Btn id="lab-pos-invalid" on={posInvalid} onClick={() => setPosInvalid((v) => !v)}>Position invalide</Btn>
        <Btn id="lab-demo2" on={showDemo2} onClick={() => setShowDemo2((v) => !v)}>DEMO-2 présent</Btn>
        <Btn id="lab-replay-heading" on={replayHeading} onClick={() => setReplayHeading((v) => !v)}>Cap relecture</Btn>
        <Btn id="lab-minimap" on={showMini} onClick={() => setShowMini((v) => !v)}>Mini-carte</Btn>
        <span data-testid="lab-probe" className="font-mono text-[10px] text-[#A3A3A3]">{probe ? `clic ${probe.lat}, ${probe.lng} · px ${probe.x},${probe.y}` : "clic carte : —"}</span>
      </div>
      <div className="flex-1 flex flex-col md:flex-row gap-2 min-h-0">
      <div className="relative flex-1 min-h-[320px] min-w-0 border border-white/10 rounded-sm overflow-hidden">
        <MapView drones={showDemo2 ? [drone, drone2, drone3] : [drone, drone3]} selectedId="DEMO-1" followId={follow ? "DEMO-1" : null} onFollowChange={setFollow}
          waypoints={WP.map((w, i) => ({ ...w, seq: i, alt: 30 }))} plannedPath={[[C.lat, C.lng], ...WP.map((w) => [w.lat, w.lng])]}
          realPath={replayPts.map((p) => [p.lat, p.lng])} cursorPoint={replayPts[replayPts.length - 1]} cursorNav={cursorNav}
          homePoint={HOME} basePoint={BASE} onMapReady={setMap} />
      </div>
      <div ref={cockpitRef} data-testid="lab-cockpit-area" className="relative w-full h-[260px] md:h-auto md:w-[420px] shrink-0 border border-white/10 rounded-sm overflow-hidden bg-[#0B1118]">
        <div className="absolute top-2 left-2 font-mono text-[9px] text-white/40 uppercase">Zone cockpit (démo)</div>
        {showMini && <CockpitMiniMap drone={drone} trace={null} homePoint={HOME} configuredBasePoint={BASE} cockpitRef={cockpitRef} onHide={() => setShowMini(false)} />}
      </div>
      </div>
    </div>
  );
}
